/**
 * MH-18 Tạo và sửa lịch phát (mục 6.2 đặc tả giao diện, Bảng 6.1).
 *
 * Biểu mẫu năm nhóm: bản tin phát, phạm vi địa bàn, thời điểm phát (nhiều khung
 * giờ trong một ngày), lặp lại (không lặp / theo ngày trong tuần / theo khoảng
 * ngày), mức ưu tiên (có giải thích hệ quả ngay cạnh trường chọn).
 *
 * Đơn vị thời gian đúng `mo-rong-lich.ts`: mọi mốc là GIÂY UTC, ngày là 00:00
 * UTC, khung giờ trong ngày là giây tính từ đầu ngày (0..86400).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { banTinApi, LOAI_BAN_TIN_CUM_LOA, type DongBanTin } from '../api/ban-tin-api';
import {
  lichPhatApi,
  type DiaBanNhanDV,
  type LichPhatNoiBoVao,
  type LoaiLich,
  type MucDoUuTien,
} from '../api/lich-phat-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BoChonPhamViDiaBan, nhanPhamVi } from '../thanh-phan/BoChonPhamViDiaBan';
import { LoiApi } from '../api/thiet-bi-api';
import { dungPhien } from '../trang-thai/ngu-canh-phien';

export interface MH18Props {
  api?: Pick<typeof lichPhatApi, 'tao'>;
  banTinApiGoi?: Pick<typeof banTinApi, 'danhSach'>;
}

interface KhungGio {
  batDau: string; // "HH:mm"
  ketThuc: string;
}

const KHUNG_GIO_RONG: KhungGio = { batDau: '06:30', ketThuc: '06:45' };

function giayTrongNgay(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 3600 + (m || 0) * 60;
}

function epochDauNgay(ngayIso: string): number {
  return Math.floor(new Date(`${ngayIso}T00:00:00Z`).getTime() / 1000);
}

const THU_TRONG_TUAN = [
  { gt: 1, nhan: 'Thứ Hai' },
  { gt: 2, nhan: 'Thứ Ba' },
  { gt: 3, nhan: 'Thứ Tư' },
  { gt: 4, nhan: 'Thứ Năm' },
  { gt: 5, nhan: 'Thứ Sáu' },
  { gt: 6, nhan: 'Thứ Bảy' },
  { gt: 7, nhan: 'Chủ Nhật' },
];

export function MH18TaoSuaLichPhat({ api, banTinApiGoi }: MH18Props = {}): ReactNode {
  const goi = api ?? lichPhatApi;
  const banTinGoi = banTinApiGoi ?? banTinApi;
  const dieuHuong = useNavigate();
  const [thamSo] = useSearchParams();

  const banTinDuyet = dungTaiDuLieu<DongBanTin[]>({
    tai: async (signal) => {
      const kq = await banTinGoi.danhSach(
        { trangThai: 'da_duyet', loaiBanTin: LOAI_BAN_TIN_CUM_LOA, pageSize: 50 },
        signal,
      );
      return kq.items;
    },
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const [tenLichPhat, datTenLichPhat] = useState('');
  const [banTinId, datBanTinId] = useState(thamSo.get('banTinId') ?? '');
  const phien = dungPhien();
  // Tài khoản cấp xã: phạm vi mặc định là xã được phân công.
  const [phamVi, datPhamVi] = useState<string[]>(() => (phien.khoaDiaBan ? [phien.khoaDiaBan.ma] : []));
  const [moPhamVi, datMoPhamVi] = useState(false);
  const [ngay, datNgay] = useState('');
  const [khungGio, datKhungGio] = useState<KhungGio[]>([KHUNG_GIO_RONG]);
  const [loaiLap, datLoaiLap] = useState<'khong_lap' | 'theo_tuan' | 'theo_khoang'>('khong_lap');
  const [ngayBatDau, datNgayBatDau] = useState('');
  const [ngayKetThuc, datNgayKetThuc] = useState('');
  const [thu, datThu] = useState<number[]>([]);
  const [mucDoUuTien, datMucDoUuTien] = useState<MucDoUuTien>('2');
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const [canhBaoTrung, datCanhBaoTrung] = useState<{
    coTrung: boolean;
    lichThucThi?: { tenLichPhat?: string } | null;
  } | null>(null);

  const banTinDaChon = useMemo(
    () => (banTinDuyet.duLieu ?? []).find((b) => b.banTinId === banTinId),
    [banTinDuyet.duLieu, banTinId],
  );

  const themKhungGio = (): void => datKhungGio((cu) => [...cu, { ...KHUNG_GIO_RONG }]);
  const xoaKhungGio = (i: number): void => datKhungGio((cu) => cu.filter((_, j) => j !== i));

  const toggleThu = (gt: number): void => {
    datThu((cu) => (cu.includes(gt) ? cu.filter((x) => x !== gt) : [...cu, gt].sort()));
  };

  const guiLuu = async (): Promise<void> => {
    datLoi(null);
    datCanhBaoTrung(null);
    if (!tenLichPhat.trim() || !banTinId || phamVi.length === 0) {
      datLoi('Cần tên lịch phát, bản tin và phạm vi địa bàn');
      return;
    }
    const phamViDV: DiaBanNhanDV[] = phamVi.map((ma) => ({ DichID: ma }));
    let than: LichPhatNoiBoVao;
    if (loaiLap === 'khong_lap') {
      if (!ngay) {
        datLoi('Cần chọn ngày phát');
        return;
      }
      const dauNgay = epochDauNgay(ngay);
      than = {
        tenLichPhat,
        banTinIds: [banTinId],
        phamVi: phamViDV,
        mucDoUuTien,
        loaiLich: 'theo_ngay' as LoaiLich,
        danhSachNgayPhat: [
          {
            NgayPhat: dauNgay,
            ThoiDiemPhat: khungGio.map((k) => ({
              ThoiGianBatDau: dauNgay + giayTrongNgay(k.batDau),
              ThoiGianKetThuc: dauNgay + giayTrongNgay(k.ketThuc),
            })),
          },
        ],
      };
    } else {
      if (!ngayBatDau || !ngayKetThuc) {
        datLoi('Cần chọn khoảng ngày lặp');
        return;
      }
      than = {
        tenLichPhat,
        banTinIds: [banTinId],
        phamVi: phamViDV,
        mucDoUuTien,
        loaiLich: 'hang_ngay' as LoaiLich,
        ngayBatDauPhat: epochDauNgay(ngayBatDau),
        ngayKetThucPhat: epochDauNgay(ngayKetThuc),
        thuTrongTuan: loaiLap === 'theo_tuan' ? thu : [],
        khungGioTrongNgay: khungGio.map((k) => ({
          thoiGianBatDau: giayTrongNgay(k.batDau),
          thoiGianKetThuc: giayTrongNgay(k.ketThuc),
        })),
      };
    }

    datDangGui(true);
    try {
      const ketQua = await goi.tao(than);
      if (ketQua.canhBaoTrung) {
        datCanhBaoTrung(
          ketQua.canhBaoTrung as { coTrung: boolean; lichThucThi?: { tenLichPhat?: string } | null },
        );
      }
      dieuHuong('/lich-phat/tuan');
    } catch (e: unknown) {
      datLoi(e instanceof LoiApi ? e.message : e instanceof Error ? e.message : 'Không lưu được lịch phát');
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-hinh" data-man-hinh="MH-18">
      <h1>{vi.lichPhat.taoLichPhat}</h1>

      <label htmlFor="lp-ten">Tên lịch phát</label>
      <input id="lp-ten" value={tenLichPhat} onChange={(e) => datTenLichPhat(e.target.value)} />

      <label htmlFor="lp-ban-tin">{vi.lichPhat.nhanBanTin}</label>
      <select id="lp-ban-tin" value={banTinId} onChange={(e) => datBanTinId(e.target.value)}>
        <option value="">—</option>
        {(banTinDuyet.duLieu ?? []).map((b) => (
          // Giá trị PHẢI là mã nghiệp vụ `banTinId` (không phải UUID `id`):
          // `batBuocBanTinDaDuyet` ở `lich-phat.service.ts` tra theo cột
          // `ban_tin_id`, đúng hình dạng `LichPhatNoiBoVao.banTinIds`.
          <option key={b.id} value={b.banTinId}>
            {b.tieuDe}
          </option>
        ))}
      </select>
      {banTinDaChon && banTinDaChon.trangThai !== 'da_duyet' ? (
        <p role="alert">{vi.lichPhat.banTinChuaDuyet}</p>
      ) : null}

      <span id="lp-pham-vi-nhan">{vi.lichPhat.nhanPhamVi}</span>
      <p>{phamVi.length === 0 ? vi.bienTap.chuaChonPhamVi : nhanPhamVi(phamVi, phien.tenDiaBanTheoMa)}</p>
      <button
        type="button"
        className="nut"
        aria-describedby="lp-pham-vi-nhan"
        onClick={() => datMoPhamVi(true)}
      >
        {vi.bienTap.chonPhamVi}
      </button>

      <fieldset>
        <legend>{vi.lichPhat.nhanLapLai}</legend>
        <label>
          <input
            type="radio"
            name="lp-lap"
            checked={loaiLap === 'khong_lap'}
            onChange={() => datLoaiLap('khong_lap')}
          />
          {vi.lichPhat.khongLap}
        </label>
        <label>
          <input
            type="radio"
            name="lp-lap"
            checked={loaiLap === 'theo_tuan'}
            onChange={() => datLoaiLap('theo_tuan')}
          />
          {vi.lichPhat.lapTheoNgayTrongTuan}
        </label>
        <label>
          <input
            type="radio"
            name="lp-lap"
            checked={loaiLap === 'theo_khoang'}
            onChange={() => datLoaiLap('theo_khoang')}
          />
          {vi.lichPhat.lapTheoKhoangNgay}
        </label>
      </fieldset>

      {loaiLap === 'khong_lap' ? (
        <>
          <label htmlFor="lp-ngay">Ngày phát</label>
          <input id="lp-ngay" type="date" value={ngay} onChange={(e) => datNgay(e.target.value)} />
        </>
      ) : (
        <>
          <label htmlFor="lp-ngay-bd">{vi.lichPhat.nhanNgayBatDau}</label>
          <input
            id="lp-ngay-bd"
            type="date"
            value={ngayBatDau}
            onChange={(e) => datNgayBatDau(e.target.value)}
          />
          <label htmlFor="lp-ngay-kt">{vi.lichPhat.nhanNgayKetThuc}</label>
          <input
            id="lp-ngay-kt"
            type="date"
            value={ngayKetThuc}
            onChange={(e) => datNgayKetThuc(e.target.value)}
          />
          {loaiLap === 'theo_tuan' ? (
            <fieldset>
              <legend>Chọn thứ trong tuần</legend>
              {THU_TRONG_TUAN.map((t) => (
                <label key={t.gt}>
                  <input type="checkbox" checked={thu.includes(t.gt)} onChange={() => toggleThu(t.gt)} />
                  {t.nhan}
                </label>
              ))}
            </fieldset>
          ) : null}
        </>
      )}

      <fieldset>
        <legend>{vi.lichPhat.nhanThoiDiemPhat}</legend>
        {khungGio.map((k, i) => (
          <div key={i} data-vung={`khung-gio-${i}`}>
            <label htmlFor={`kg-bd-${i}`}>Bắt đầu</label>
            <input
              id={`kg-bd-${i}`}
              type="time"
              value={k.batDau}
              onChange={(e) =>
                datKhungGio((cu) => cu.map((x, j) => (j === i ? { ...x, batDau: e.target.value } : x)))
              }
            />
            <label htmlFor={`kg-kt-${i}`}>Kết thúc</label>
            <input
              id={`kg-kt-${i}`}
              type="time"
              value={k.ketThuc}
              onChange={(e) =>
                datKhungGio((cu) => cu.map((x, j) => (j === i ? { ...x, ketThuc: e.target.value } : x)))
              }
            />
            {khungGio.length > 1 ? (
              <button type="button" className="nut nut--nho" onClick={() => xoaKhungGio(i)}>
                {vi.lichPhat.xoaKhungGio}
              </button>
            ) : null}
          </div>
        ))}
        <button type="button" className="nut" onClick={themKhungGio}>
          {vi.lichPhat.themKhungGio}
        </button>
      </fieldset>

      <label htmlFor="lp-uu-tien">{vi.lichPhat.nhanMucDoUuTien}</label>
      <select
        id="lp-uu-tien"
        value={mucDoUuTien}
        onChange={(e) => datMucDoUuTien(e.target.value as MucDoUuTien)}
      >
        <option value="1">{vi.lichPhat.uuTienNhan['1']}</option>
        <option value="2">{vi.lichPhat.uuTienNhan['2']}</option>
        <option value="3">{vi.lichPhat.uuTienNhan['3']}</option>
      </select>
      <p>{vi.lichPhat.uuTienGiaiThich}</p>

      {loi ? (
        <p role="alert" data-vung="loi-luu-lich">
          {loi}
        </p>
      ) : null}
      {canhBaoTrung?.coTrung ? (
        <p role="alert" data-vung="canh-bao-trung">
          {dienVao(vi.lichPhat.xungDotGiaiThich, {
            ten: tenLichPhat,
            uuTien: mucDoUuTien,
            thang: canhBaoTrung.lichThucThi?.tenLichPhat ?? '—',
          })}
        </p>
      ) : null}

      <div className="man-hinh__hanh-dong">
        <button type="button" className="nut" onClick={() => dieuHuong('/lich-phat/tuan')}>
          {vi.lichPhat.huy}
        </button>
        <button type="button" className="nut nut--chinh" disabled={dangGui} onClick={() => void guiLuu()}>
          {vi.lichPhat.luu}
        </button>
      </div>

      {moPhamVi ? (
        <BoChonPhamViDiaBan
          giaTri={phamVi}
          onXacNhan={(ds) => {
            datPhamVi(ds);
            datMoPhamVi(false);
          }}
          onHuy={() => datMoPhamVi(false)}
        />
      ) : null}
    </div>
  );
}
